/**
 * queue/stateStyle.ts
 * Monochrome state styling for entries — ported from the prototype's
 * `stateStyle()`. State is communicated WITHOUT colour: hollow ring (waiting),
 * solid black chip + pulse (called), filled dot (seated), dashed + strikethrough
 * (no-show), etc. Returns plain RN style objects + a label + a pulse flag.
 */

import type { ViewStyle, TextStyle } from 'react-native';
import type { EntryState } from './types';
import { LightColors as C } from '../theme/colors';

export interface StateChipStyle {
  chip: ViewStyle;
  chipText: TextStyle;
  dot: ViewStyle;
  label: string;
  pulse: boolean; // dot/chip pulses (called / you're-next)
}

const baseChip: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  height: 23,
  paddingHorizontal: 10,
  borderRadius: 999,
  borderWidth: 1,
};
const baseChipText: TextStyle = {
  fontFamily: 'IBM Plex Sans',
  fontSize: 11,
  fontWeight: '600',
};
const baseDot: ViewStyle = { width: 7, height: 7, borderRadius: 999, marginRight: 6 };

export function stateStyle(st: EntryState): StateChipStyle {
  switch (st) {
    case 'WAITING':
      return {
        chip: { ...baseChip, backgroundColor: C.card, borderColor: C.lineStrong },
        chipText: { ...baseChipText, color: C.ink2 },
        dot: { ...baseDot, borderWidth: 1.5, borderColor: C.ink3, backgroundColor: 'transparent' },
        label: 'Waiting', pulse: false,
      };
    case 'CALLED':
      return {
        chip: { ...baseChip, backgroundColor: C.ink, borderColor: C.ink },
        chipText: { ...baseChipText, color: C.white },
        dot: { ...baseDot, backgroundColor: C.white },
        label: "You're next", pulse: true,
      };
    case 'ACKNOWLEDGED':
      // "On the way" — solid mini-chip (guest replied to the page); still held.
      return {
        chip: { ...baseChip, backgroundColor: C.ink, borderColor: C.ink },
        chipText: { ...baseChipText, color: C.white },
        dot: { ...baseDot, backgroundColor: C.white },
        label: 'On the way', pulse: false,
      };
    case 'ALLOCATED':
      return {
        chip: { ...baseChip, backgroundColor: C.ink, borderColor: C.ink },
        chipText: { ...baseChipText, color: C.white },
        dot: { ...baseDot, backgroundColor: C.white },
        label: 'Ready', pulse: false,
      };
    // SERVING (rev 5) / SEATED (legacy alias) — filled dot on paper-2.
    case 'SERVING':
    case 'SEATED':
      return {
        chip: { ...baseChip, backgroundColor: C.paper2, borderColor: C.lineStrong },
        chipText: { ...baseChipText, color: C.ink },
        dot: { ...baseDot, backgroundColor: C.ink },
        label: st === 'SERVING' ? 'Serving' : 'Seated', pulse: false,
      };
    // DONE (rev 5) / RELEASED (legacy alias) — greyed, finished.
    case 'DONE':
    case 'RELEASED':
      return {
        chip: { ...baseChip, backgroundColor: C.card, borderColor: C.line },
        chipText: { ...baseChipText, color: C.ink4 },
        dot: { ...baseDot, backgroundColor: C.lineStrong },
        label: st === 'DONE' ? 'Done' : 'Released', pulse: false,
      };
    case 'CANCELLED':
      return {
        chip: { ...baseChip, backgroundColor: C.card, borderColor: C.lineStrong, borderStyle: 'dashed' },
        chipText: { ...baseChipText, color: C.ink4 },
        dot: { width: 7, height: 7 },
        label: 'Cancelled', pulse: false,
      };
    case 'NO_SHOW':
      return {
        chip: { ...baseChip, backgroundColor: C.card, borderColor: C.ink3, borderStyle: 'dashed' },
        chipText: { ...baseChipText, color: C.ink3, textDecorationLine: 'line-through' },
        dot: { width: 7, height: 7 },
        label: 'No-show', pulse: false,
      };
    case 'REMOVED':
      return {
        chip: { ...baseChip, backgroundColor: C.card, borderColor: C.lineStrong, borderStyle: 'dashed' },
        chipText: { ...baseChipText, color: C.ink4 },
        dot: { width: 7, height: 7 },
        label: 'Removed', pulse: false,
      };
    default:
      return {
        chip: { ...baseChip, backgroundColor: C.card, borderColor: C.lineStrong },
        chipText: { ...baseChipText, color: C.ink2 },
        dot: { ...baseDot, backgroundColor: C.ink4 },
        label: st, pulse: false,
      };
  }
}

// Channel display labels (prototype's chLabel).
export const CHANNEL_LABEL: Record<string, string> = {
  QR: 'QR', VOICE: 'Voice', STAFF: 'Walk-in', WEB: 'Web', WHATSAPP: 'WhatsApp',
  CHAT: 'Chat', KIOSK: 'Kiosk', API: 'API',
};
